import { FC, useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import { Menu, Preloader, TrainCard, Trains } from "@/components";
import { useCreateTrainingMutation, useDeleteTrainingMutation } from "@/store/trainingsApi";
import { Training } from "@/components/Trains/model";
import { useTrainerChatOutlet } from "./TrainerChatUserLayout";
import { CreateTrainingModal } from "@/features/create-training-modal";
import { ConfirmDeleteModal } from "@/features/confirm-delete-modal";
import { tg } from "@/constants/telegram";

import styles from "./TrainerChatTrainingsProgramPage.module.scss";

export const TrainerChatTrainingsProgramPage: FC = () => {
  const { userId, profile } = useTrainerChatOutlet();
  const navigate = useNavigate();
  const [initData] = useState(tg.initData || "");

  const [isLoading, setIsLoading] = useState(true);
  const [userTrainings, setUserTrainings] = useState<Training[]>([]);
  const [scheduleTrainings, setScheduleTrainings] = useState<Training[]>([]);
  const [trainingCount, setTrainingCount] = useState<number>(0);
  const [activeTab, setActiveTab] = useState<"user" | "ai">("user");
  const [showCreateTrainingModal, setShowCreateTrainingModal] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [trainingToDelete, setTrainingToDelete] = useState<number | null>(null);

  const [createTraining] = useCreateTrainingMutation();
  const [deleteTraining] = useDeleteTrainingMutation();

  const getTrainings = useCallback(async () => {
    try {
      const trainResult = await createTraining({ 
        fromUser: userId,
      }).unwrap();
      
      const trainingsData = trainResult.trainings || {};
      const loadedUserTrainings = Array.isArray(trainingsData.created_by_user) 
        ? trainingsData.created_by_user 
        : [];
      const loadedScheduleTrainings = Array.isArray(trainingsData.schedule) 
        ? trainingsData.schedule 
        : [];
      
      setUserTrainings(loadedUserTrainings);
      setScheduleTrainings(loadedScheduleTrainings);
      setTrainingCount(trainResult.training_count || 0);
    } catch (error) {
      console.error("Error loading trainings:", error);
      toast.error("Не удалось загрузить тренировки");
    } finally {
      setIsLoading(false);
    }
  }, [userId, createTraining]);

  useEffect(() => {
    getTrainings();
  }, [getTrainings]);

  const handleDeleteTraining = async (trainingId: number) => {
    try {
      await deleteTraining({
        training_id: trainingId,
        fromUser: userId,
      }).unwrap();
      toast.success("Тренировка удалена");
      getTrainings();
    } catch (error: any) {
      console.error("Ошибка удаления тренировки:", error);
      toast.error(error?.data?.detail || "Ошибка при удалении тренировки");
    }
  };

  const handleCreateTrainingSuccess = () => {
    getTrainings();
  };

  if (isLoading) {
    return (
      <div className={styles.loader}>
        <Preloader />
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <Link to=".." className={styles.back}>
          ← Назад к клиенту
        </Link>
        <h3>Программа тренировок</h3>
        <span className={styles.placeholder} />
      </div>

      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${activeTab === "user" ? styles.tabActive : ""}`}
          onClick={() => setActiveTab("user")}
        >
          Мои тренировки
        </button>
        <button
          className={`${styles.tab} ${activeTab === "ai" ? styles.tabActive : ""}`}
          onClick={() => setActiveTab("ai")}
        >
          ИИ тренер
        </button>
      </div>

      {activeTab === "user" && (
        <div>
          {userTrainings.length > 0 ? (
            <>
              <div style={{ marginBottom: "20px", textAlign: "center" }}>
                <button
                  className={styles.createTrainingBtn}
                  onClick={() => setShowCreateTrainingModal(true)}
                >
                  + Создать тренировку
                </button>
              </div>
              <Trains 
                trainings={userTrainings} 
                hasActiveSubscription={true} 
                trainingCount={trainingCount}
                showDeleteButton={true}
                onDeleteTraining={(trainingId) => {
                  setTrainingToDelete(trainingId);
                  setDeleteModalOpen(true);
                }}
                isAdmin={true}
                adminUserId={userId}
                editBasePath="/trainer/clients"
              />
            </>
          ) : (
            <div style={{ textAlign: "center", padding: "40px 20px" }}>
              <div style={{ marginBottom: "20px" }}>
                <button
                  className={styles.createTrainingBtn}
                  onClick={() => setShowCreateTrainingModal(true)}
                >
                  + Создать тренировку
                </button>
              </div>
              <p style={{ color: "#666", fontSize: "16px" }}>
                У пользователя пока нет созданных тренировок
              </p>
            </div>
          )}
        </div>
      )}

      {activeTab === "ai" && (
        <div>
          {scheduleTrainings.length > 0 ? (
            <Trains 
              trainings={scheduleTrainings} 
              hasActiveSubscription={true} 
              trainingCount={trainingCount}
              showDeleteButton={false}
              isAdmin={true}
              adminUserId={userId}
              editBasePath="/trainer/clients"
            />
          ) : (
            <div style={{ textAlign: "center", padding: "40px 20px" }}>
              <p style={{ color: "#666", fontSize: "16px" }}>
                У пользователя пока нет сгенерированных тренировок
              </p>
            </div>
          )}
        </div>
      )}

      {showCreateTrainingModal && (
        <CreateTrainingModal
          initData={initData}
          onClose={() => setShowCreateTrainingModal(false)}
          onSuccess={handleCreateTrainingSuccess}
          fromUser={userId}
        />
      )}

      {deleteModalOpen && trainingToDelete !== null && (
        <ConfirmDeleteModal
          onClose={() => {
            setDeleteModalOpen(false);
            setTrainingToDelete(null);
          }}
          onConfirm={() => {
            handleDeleteTraining(trainingToDelete);
            setDeleteModalOpen(false);
            setTrainingToDelete(null);
          }}
          title="Удаление тренировки"
          message="Вы уверены, что хотите удалить эту тренировку?"
        />
      )}
    </div>
  );
};


